// Cache local (par mail) de la réponse générée/éditée et de la consigne en cours de saisie.
// But : éviter de perdre ce travail quand on navigue d'un mail à l'autre, ce qui recharge
// entièrement le taskpane (pas d'événement ItemChanged géré ici) et forcerait sinon une
// régénération pour rien. Volontairement borné aux N derniers mails (LRU) plutôt que vidé à la
// fermeture d'Outlook, car Office.js ne fournit aucun événement fiable pour détecter cette fermeture.

const STORAGE_KEY = "praetoria_reply_cache_v1";
const MAX_ENTRIES = 10;
// Correspondance notariale : ne pas garder le texte des réponses indéfiniment en clair dans
// localStorage. Purge best-effort basée sur l'âge de l'entrée (pas d'événement de fermeture
// fiable côté Office.js pour vider le cache autrement -- voir commentaire en tête de fichier).
const ENTRY_TTL_MS = 24 * 60 * 60 * 1000;

export interface CachedReplyState {
  instruction: string;
  hasReply: boolean;
  replyText: string;
  originalAiReply: string | null;
  lastSentEditedReply: string | null;
  isReplyEditable: boolean;
  replyFeedback: "up" | "down" | null;
  feedbackComment: string;
}

interface StoredEntry {
  savedAt: number;
  state: CachedReplyState;
}

interface CacheStore {
  order: string[];
  entries: { [itemKey: string]: StoredEntry };
}

function readStore(): CacheStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { order: [], entries: {} };
    }
    const parsed = JSON.parse(raw) as Partial<CacheStore>;
    if (!parsed || !Array.isArray(parsed.order) || typeof parsed.entries !== "object" || !parsed.entries) {
      return { order: [], entries: {} };
    }
    return { order: parsed.order, entries: parsed.entries as { [itemKey: string]: StoredEntry } };
  } catch {
    return { order: [], entries: {} };
  }
}

// Purge les brouillons trop vieux (horodatage individuel `savedAt`, pas un TTL global déclenché
// par un timer) -- appelée uniquement à l'ouverture (loadCachedReply), pas à chaque frappe/sauvegarde,
// pour ne pas payer ce balayage à chaque persist.
function purgeExpired(store: CacheStore): CacheStore {
  const now = Date.now();
  const entries: { [itemKey: string]: StoredEntry } = {};
  const order: string[] = [];
  let didExpire = false;
  for (const key of store.order) {
    const entry = store.entries[key] as Partial<StoredEntry> | undefined;
    // Ancien format sans `savedAt` (avant ce fix) : traité comme expiré plutôt que gardé
    // indéfiniment par défaut.
    if (!entry || typeof entry.savedAt !== "number" || now - entry.savedAt > ENTRY_TTL_MS) {
      didExpire = true;
      continue;
    }
    entries[key] = entry as StoredEntry;
    order.push(key);
  }
  if (!didExpire) {
    return store;
  }
  const purged = { order, entries };
  writeStore(purged);
  return purged;
}

function writeStore(store: CacheStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Quota dépassé ou stockage indisponible (navigation privée) : le cache reste best-effort.
  }
}

export function loadCachedReply(itemKey: string): CachedReplyState | null {
  if (!itemKey) {
    return null;
  }
  const store = purgeExpired(readStore());
  return store.entries[itemKey]?.state || null;
}

export function saveCachedReply(itemKey: string, state: CachedReplyState): void {
  if (!itemKey) {
    return;
  }
  const store = readStore();
  store.entries[itemKey] = { savedAt: Date.now(), state };
  store.order = [itemKey].concat(store.order.filter((key) => key !== itemKey));
  while (store.order.length > MAX_ENTRIES) {
    const evictedKey = store.order.pop();
    if (evictedKey) {
      delete store.entries[evictedKey];
    }
  }
  writeStore(store);
}

export function clearCachedReply(itemKey: string): void {
  if (!itemKey) {
    return;
  }
  const store = readStore();
  if (!(itemKey in store.entries)) {
    return;
  }
  delete store.entries[itemKey];
  store.order = store.order.filter((key) => key !== itemKey);
  writeStore(store);
}
